import { useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import {
  CreatePatientContactRequestSchema,
  type CreatePatientContactRequest,
} from '@ctms/contracts';
import { z } from 'zod';
import { AlertCircle, X } from 'lucide-react';

import { getApiErrorMessage } from '../../../api/client.js';
import { PersonnelSelect } from '../../trials/personnel-select/personnel-select.js';
import { visitsApi } from '../visits.api.js';

/** One entry of the "Powiąż z wizytą" list — the plan row named `V<n>`. */
export interface ContactVisitOption {
  id: number;
  label: string;
}

/**
 * The note is the only required text (REQ-VIS-038: a contact without content
 * does not save); the phone number is optional (meaningful mostly for an SMS).
 * Dates are never asked for — the server stamps `contactedAt` (REQ-VIS-039).
 * `visitId` travels as a string so the "Bez wizyty" placeholder stays valid:
 * an empty value becomes an explicit `patientVisitId: null`.
 */
const ContactFormSchema = z.object({
  mode: z.enum(['S', 'P']),
  text: z.string().trim().min(1, 'Notatka / treść jest wymagana.').max(2000, 'Notatka może mieć najwyżej 2000 znaków.'),
  phoneNumber: z.string().max(64, 'Numer może mieć najwyżej 64 znaki.').optional(),
  visitId: z.string(),
  personnelId: z.string().min(1, 'Wybierz osobę.'),
});

type ContactFormInput = z.infer<typeof ContactFormSchema>;

export interface LogContactDialogProps {
  /** The plan rows of the active participation, listed as "Powiąż z wizytą" (`V<n>`) plus "Bez wizyty". */
  visits: ContactVisitOption[];
  /** Patient name for the header (presentation only). */
  patientName: string | null;
  /** Called after the server stored the contact — the caller may refresh related views. */
  onSaved: () => void;
  onClose: () => void;
}

const fieldClass =
  'mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-100';
const labelClass = 'text-xs text-slate-400';

/**
 * "Kontakt SMS/telefon" — records a phone or SMS contact (REQ-VIS-038,
 * `#log-contact` / `#save-contact`). The visit link is optional: "Bez wizyty"
 * is a legal contact (legacy `Contact.Patient_visit_id` is nullable), though such
 * a row carries no patient link and therefore does not appear on the patient's
 * contact list (session-4 accepted debt, documented in `docs/`).
 */
export function LogContactDialog({ visits, patientName, onSaved, onClose }: LogContactDialogProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ContactFormInput>({
    resolver: zodResolver(ContactFormSchema),
    defaultValues: { mode: 'P', text: '', phoneNumber: '', visitId: '', personnelId: '' },
  });

  const saveMutation = useMutation({
    mutationFn: (request: CreatePatientContactRequest) => visitsApi.createPatientContact(request),
    onSuccess: () => {
      onSaved();
    },
    onError: async (err: unknown) => {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się zapisać kontaktu.'));
    },
  });

  const isSubmitting = saveMutation.isPending;
  const mode = watch('mode');

  const onSubmit = (input: ContactFormInput): void => {
    setErrorMessage(null);
    const phone = input.phoneNumber?.trim() ?? '';
    const request: CreatePatientContactRequest = {
      patientVisitId: input.visitId === '' ? null : Number(input.visitId),
      personnelId: Number(input.personnelId),
      mode: input.mode,
      phoneNumber: phone.length > 0 ? phone : null,
      text: input.text.trim(),
    };
    const parsed = CreatePatientContactRequestSchema.safeParse(request);
    if (!parsed.success) {
      setErrorMessage('Nie udało się zapisać kontaktu.');
      return;
    }
    saveMutation.mutate(parsed.data);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="log-contact-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-4">
          <h2 id="log-contact-title" className="text-base font-semibold text-slate-100">
            Kontakt z pacjentem{patientName ? ` — ${patientName}` : ''}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Zamknij"
            className="rounded-md p-1.5 text-slate-400 hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form
          onSubmit={(event) => {
            void handleSubmit(onSubmit)(event);
          }}
          className="mt-5 space-y-4"
          noValidate
        >
          {errorMessage !== null && (
            <div
              role="alert"
              className="flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-xs text-rose-300"
            >
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <fieldset className="flex items-center gap-4">
            <legend className={labelClass}>Kanał kontaktu</legend>
            <label htmlFor="contact-mode-sms" className="flex items-center gap-1.5 text-xs text-slate-200">
              <input id="contact-mode-sms" type="radio" value="S" disabled={isSubmitting} {...register('mode')} />
              SMS
            </label>
            <label htmlFor="contact-mode-phone" className="flex items-center gap-1.5 text-xs text-slate-200">
              <input id="contact-mode-phone" type="radio" value="P" disabled={isSubmitting} {...register('mode')} />
              Telefon
            </label>
          </fieldset>

          <div>
            <label htmlFor="contact-text" className={labelClass}>
              Notatka / treść
            </label>
            <textarea
              id="contact-text"
              disabled={isSubmitting}
              className={`${fieldClass} min-h-20`}
              {...register('text')}
            />
            {errors.text !== undefined && <p className="mt-1 text-xs text-rose-400">{errors.text.message}</p>}
          </div>

          <div>
            <label htmlFor="contact-phone" className={labelClass}>
              Numer (opcjonalnie{mode === 'S' ? '' : ', głównie dla SMS'})
            </label>
            <input id="contact-phone" type="text" disabled={isSubmitting} className={fieldClass} {...register('phoneNumber')} />
            {errors.phoneNumber !== undefined && (
              <p className="mt-1 text-xs text-rose-400">{errors.phoneNumber.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="contact-visit" className={labelClass}>
              Powiąż z wizytą
            </label>
            <select id="contact-visit" className={fieldClass} disabled={isSubmitting} {...register('visitId')}>
              <option value="">Bez wizyty</option>
              {visits.map((visit) => (
                <option key={visit.id} value={String(visit.id)}>
                  {visit.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <PersonnelSelect id="contact-person" registration={register('personnelId')} disabled={isSubmitting} />
            {errors.personnelId !== undefined && (
              <p className="mt-1 text-xs text-rose-400">{errors.personnelId.message}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
            <button
              type="button"
              id="cancel-contact"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
            >
              Anuluj
            </button>
            <button
              type="submit"
              id="save-contact"
              disabled={isSubmitting}
              className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
            >
              {isSubmitting ? 'Zapisywanie…' : 'Zapisz kontakt'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}